import moment from "moment-with-locales-es6";
import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

import { useOnInit } from "../../utils/useOnInit";
import { DomainContext } from "../../domain-repository/DomainRepository";
import { driverInfoReducer } from "./driverInfoReducer";
import {
  checkDriverInfoStepEditMode,
  COUNTRY_CODE_PREFIX,
  getFormatedPhoneAndCountryCode,
  useFocusChain,
  validValidationObject,
} from "./driverInfoStepUtils";
import {
  validateEmail,
  validateEmailConfirm,
  validateFullName,
  validateNotEmpty,
  validatePhoneCountryCode,
} from "./driver-fields-validations";
import { padZero } from "../../utils/utils";

import PageHeader from "../../components/PageHeader";
import Footer from "../../components/Footer";
import TrackingPage from "../../components/TrackingPage";
import withLang from "../../withLang";
import { DriverInfoPage } from "./components/DriverInfoPage";
import { LabeledDriverInfoInput } from "./components/DriverInfoInput";
import { Spacer } from "../../components/Spacer";
import DriverDateInput from "./components/DriverDateInput";

const FIELD_ORDER = [
  "fullName",
  "birthDay",
  "birthMonth",
  "birthYear",
  "email",
  "emailConfirm",
  "phoneCountryCode",
  "phone",
];

function DriverStep2Page(props) {
  const { t } = useTranslation();
  const { registerRef, focusNext } = useFocusChain(FIELD_ORDER);

  const {
    addDriverInfo,
    driverInfo,
    getCountries,
    driverInfoHasClickedBack,
    setDriverInfoHasClickedBack,
    driverInfoEditMode,
    setDriverInfoEditMode,
    reservation,
    visitedRoutes,
    addVisitedRoutes,
    invalidBirthdates,
    isEligibleForYoungDriver,
    getDriversMinimumAgeForReservation,
    getDriversPhoneCountryCode,
  } = useContext(DomainContext);
  const countries = getCountries();

  const countryPhoneCodes = countries.filter((c) => c.PhoneCountryCode);
  const formattedPhone = getFormatedPhoneAndCountryCode(
    driverInfo.phone,
    countryPhoneCodes
  );

  const [state, dispatch] = useReducer(driverInfoReducer, {
    canDisplayErrors: false,

    fullName: driverInfo.fullName || "",
    email: driverInfo.email || "",
    emailConfirm: driverInfo.emailConfirm || "",
    birthDay:
      (driverInfo.birthDate && moment(driverInfo.birthDate).date()) || "",
    birthMonth:
      (driverInfo.birthDate && moment(driverInfo.birthDate).month() + 1) || "",
    birthYear:
      (driverInfo.birthDate && moment(driverInfo.birthDate).year()) || "",

    phone: formattedPhone.phone,
    phoneCountryCode:
      formattedPhone.phoneCountryCode ||
      `${COUNTRY_CODE_PREFIX}${getDriversPhoneCountryCode()}`,
    isLoading: null,
  });

  const [validateCanSkip, setValidateCanSkip] = useState(
    !visitedRoutes.includes(window.location.pathname) && !driverInfoEditMode && !driverInfoHasClickedBack
  );

  useEffect(() => {
    if (state.birthDay && state.birthMonth && state.birthYear) {
      const birthDate = `${state.birthYear}-${padZero(
        state.birthMonth
      )}-${padZero(state.birthDay)}`;
      if (invalidBirthdates.some((b) => b === birthDate)) {
        dispatch({
          type: "SET_VALUES",
          payload: { birthDay: "", birthMonth: "", birthYear: "" },
        });
      }
    }
  }, [invalidBirthdates, state.birthDay, state.birthMonth, state.birthYear]);

  const fullNameValidation = useMemo(
    () =>
      validateFullName(
        t,
        state.fullName,
        reservation.listReservation.DriverSurname || "",
        reservation.listReservation.DriverShortName || "",
        state.differentReservationNameCounter,
        dispatch
      ),
    [
      t,
      state.fullName,
      reservation.listReservation.DriverSurname,
      reservation.listReservation.DriverShortName,
      state.differentReservationNameCounter,
      dispatch,
    ]
  );

  const emailValidation = useMemo(
    () => validateEmail(t, state.email),
    [state.email]
  );

  const emailConfirmValidation = useMemo(
    () => validateCanSkip ? { isValid: true } : validateEmailConfirm(t, state.email, state.emailConfirm),
    [validateCanSkip, state.email, state.emailConfirm]
  );

  const phoneValidation = useMemo(
    () => validateNotEmpty(t, state.phone, "emptyPhone"),
    [state.phone]
  );

  const phoneCountryCodeValidation = useMemo(
    () => validatePhoneCountryCode(t, state.phoneCountryCode, countries),
    [state.phoneCountryCode, countries]
  );

  const birthDateValidation = useMemo(() => {
    function validateBirthDate() {
      if (
        state.birthDay &&
        state.birthMonth &&
        state.birthYear &&
        Number.isInteger(Number(state.birthDay)) &&
        Number.isInteger(Number(state.birthMonth)) &&
        Number.isInteger(Number(state.birthYear))
      ) {
        const birthDate = `${state.birthYear}-${padZero(
          state.birthMonth
        )}-${padZero(state.birthDay)}`;
        if (!moment(birthDate, "YYYY-MM-DD").isValid()) {
          return { isValid: false, message: t("invalidBirthDate") };
        }
        if (
          moment().diff(birthDate, "years") <
            getDriversMinimumAgeForReservation() &&
          !isEligibleForYoungDriver(
            birthDate,
            driverInfo.driversLicenseIssuedDate
          )
        ) {
          return { isValid: false, message: t("tooYoungToDrive") };
        }
        if (invalidBirthdates.some((b) => b === birthDate)) {
          return { isValid: false, message: t("invalidBirthDate") };
        }
        return { isValid: true };
      }
      return { isValid: false, message: t("invalidBirthDate") };
    }
    return validateBirthDate();
  }, [
    state.birthDay,
    state.birthMonth,
    state.birthYear,
    driverInfo.driversLicenseIssuedDate,
    invalidBirthdates,
    t,
  ]);

  const selectedCountry = useMemo(
    () => countries.find((c) => c.countryID === state.country?.value),
    [countries, state.country]
  );

  const canNext = useCallback(() => {
    return (
      fullNameValidation.isValid &&
      birthDateValidation.isValid &&
      emailValidation.isValid &&
      emailConfirmValidation.isValid &&
      phoneValidation.isValid &&
      phoneCountryCodeValidation.isValid &&
      birthDateValidation.isValid
    );
  }, [
    fullNameValidation,
    selectedCountry,
    emailConfirmValidation,
    emailValidation,
    birthDateValidation,
    phoneValidation,
    phoneCountryCodeValidation,
  ]);

  useEffect(() => {
    const editMode = checkDriverInfoStepEditMode(props.location.search);
    setDriverInfoEditMode(editMode);
  }, []);

  const handleProceedNextPage = useCallback(
    (type) => {
      const url =
        type === "review" ? "/driver-info-review" : "/driver-info-step/3";
      setDriverInfoHasClickedBack(false);

      const birthDate = `${state.birthYear}-${state.birthMonth}-${state.birthDay}`;
      addDriverInfo({
        phone: `${state.phoneCountryCode}${state.phone}`,
        fullName: state.fullName,
        email: state.email,
        emailConfirm: state.emailConfirm,
        birthDate,
      });

      if (type === "redirect") {
        props.history.replace(url);
      } else {
        props.history.push(url);
      }
    },
    [
      countries,
      addDriverInfo,
      setDriverInfoHasClickedBack,
      state,
      props.history,
      t,
    ]
  );

  const onNextClick = useCallback(
    (type) => {
      const canSkipPage = canNext();
      dispatch({ type: "SET_VALUES", payload: { canDisplayErrors: true } });
      if (canSkipPage) {
        handleProceedNextPage(type);
      }
    },
    [handleProceedNextPage, canNext]
  );

  const onBack = useCallback(() => {
    setDriverInfoHasClickedBack(true);
    props.history.goBack();
  }, [props.history, setDriverInfoHasClickedBack]);

  useOnInit(() => {
    if (!visitedRoutes.includes(window.location.pathname) && !driverInfoEditMode && !driverInfoHasClickedBack) {
      const canSkipPage = canNext();
      if (canSkipPage) {
        handleProceedNextPage("redirect");
      } else {
        addVisitedRoutes(window.location.pathname);
      }
    }
    setValidateCanSkip(false);
  });

  return (
    <DriverInfoPage isLoading={validateCanSkip}>
      <PageHeader
        title={t("personalInfo")}
        active={1}
        progress={"20%"}
        handleLogoClick={() => props.history.push("/start")}
      />
      <main className="page-layout">
        <div className="container grow">
          <div className="driver-content" style={{ height: "100%" }}>
            <form>
              <LabeledDriverInfoInput
                label={t("name")}
                id="fullName"
                inputRef={registerRef("fullName")}
                value={state.fullName}
                onChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { fullName: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? fullNameValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <DriverDateInput
                t={t}
                label={t("birthDate")}
                inputRef={registerRef("birthDay")}
                onComplete={() => focusNext("birthYear")}
                dayValue={state.birthDay}
                dayName="birthDay"
                monthValue={state.birthMonth}
                monthName="birthMonth"
                yearValue={state.birthYear}
                yearName="birthYear"
                onDayChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { birthDay: value },
                  })
                }
                onMonthChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { birthMonth: value },
                  })
                }
                onYearChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { birthYear: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? birthDateValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <LabeledDriverInfoInput
                label={t("email")}
                id="email"
                inputRef={registerRef("email")}
                value={state.email}
                onChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { email: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? emailValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <LabeledDriverInfoInput
                label={t("emailConfirm")}
                id="emailConfirm"
                inputRef={registerRef("emailConfirm")}
                value={state.emailConfirm}
                onChange={(value) =>
                  dispatch({
                    type: "SET_VALUES",
                    payload: { emailConfirm: value },
                  })
                }
                validationObject={
                  state.canDisplayErrors
                    ? emailConfirmValidation
                    : validValidationObject
                }
              />
              <Spacer />
              <div className="flex gap-4 items-center">
                <LabeledDriverInfoInput
                  wrapperStyle={{ width: '100px' }}
                  label={t("phoneCountryCode")}
                  name="phoneCountryCode"
                  inputRef={registerRef("phoneCountryCode")}
                  value={state.phoneCountryCode}
                  onChange={(value) =>
                    dispatch({
                      type: "SET_VALUES",
                      payload: { phoneCountryCode: value },
                    })
                  }
                  validationObject={
                    state.canDisplayErrors
                      ? phoneCountryCodeValidation
                      : validValidationObject
                  }
                />
                <LabeledDriverInfoInput
                  wrapperStyle={{ flex: '1' }}
                  label={t("phone")}
                  name="phone"
                  inputRef={registerRef("phone")}
                  value={state.phone}
                  onChange={(value) =>
                    dispatch({
                      type: "SET_VALUES",
                      payload: { phone: value },
                    })
                  }
                  validationObject={
                    state.canDisplayErrors
                      ? phoneValidation
                      : validValidationObject
                  }
                />
              </div>

              <Spacer />
            </form>
          </div>
        </div>
        <Footer
          prevHidden={driverInfoEditMode}
          handleNextClick={onNextClick}
          handlePrevClick={onBack}
          nextHighlighted={canNext()}
        />
      </main>
    </DriverInfoPage>
  );
}

export default withLang(TrackingPage(DriverStep2Page, 2005));